import { useHandler } from "@redotech/react-util/hook";
import { LicensePlateTemplate } from "@redotech/redo-inventory-common/license-plate-template";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import limitedPreviewUrl from "./license-plate-template-preview-assets/license-plate-limited-preview.png?url";
import standardPreviewUrl from "./license-plate-template-preview-assets/license-plate-standard-preview.png?url";

interface LicensePlateTemplateInputProps {
  disabled: boolean;
  setValue(value: LicensePlateTemplate): void;
  value: LicensePlateTemplate;
}

// Examples rendered by generateQrLicensePlatePdfBuffer. Regenerate them when
// either printed template changes.
const templatePreviews: Record<
  LicensePlateTemplate,
  { label: string; src: string; description: string }
> = {
  full: {
    label: "Standard",
    src: standardPreviewUrl,
    description: "Includes the product name and options.",
  },
  "item-id-only": {
    label: "Limited",
    src: limitedPreviewUrl,
    description: "Shows the merchant name, QR code, and item ID.",
  },
};

const templateOptions: RedoListItem<LicensePlateTemplate>[] = [
  {
    type: "text",
    id: "full",
    value: LicensePlateTemplate.full,
    text: templatePreviews.full.label,
  },
  {
    type: "text",
    id: "item-id-only",
    value: LicensePlateTemplate.itemIdOnly,
    text: templatePreviews["item-id-only"].label,
  },
];

export const LicensePlateTemplateInput = memo(
  function LicensePlateTemplateInput({
    disabled,
    setValue,
    value,
  }: LicensePlateTemplateInputProps) {
    const preview = templatePreviews[value];
    const imageProps = {
      alt: `${preview.label} license plate preview`,
      src: preview.src,
    };
    const selectTemplate = useHandler(
      ({ item }: { item: RedoListItem<LicensePlateTemplate> }) =>
        setValue(item.value),
    );

    return (
      <Flex dir="column" gap="md">
        <RedoSingleSelectDropdownInput<LicensePlateTemplate>
          ariaLabel="License plate template"
          description="Used for this merchant across warehouses and workstations."
          disabled={disabled}
          label="License plate template"
          options={templateOptions}
          optionSelected={selectTemplate}
          selectedItem={templateOptions.find((item) => item.value === value)}
          size="xs"
        />
        <Flex aria-live="polite" dir="column" gap="sm">
          <Flex
            {...imageProps}
            as="img"
            borderColor="secondary"
            borderRadius="sm"
            borderStyle="solid"
            borderWidth="1px"
            w="full"
          />
          <Text fontSize="xxs" textColor="secondary">
            3 × 1 in example. {preview.description}
          </Text>
        </Flex>
      </Flex>
    );
  },
);
